import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { ROUTE_ROLES, type ProtectedRoutePath } from '@/lib/auth/permissions';
import { Skeleton } from '@/ui/skeleton';

import { ProtectedShell } from './shell/ProtectedShell';
import { RequireRole } from './shell/RequireRole';

const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const BookingsPage = lazy(() => import('@/features/bookings/pages/BookingsPage'));
const QuotesPage = lazy(() => import('@/features/quotes/pages/QuotesPage'));
const SettlementsPage = lazy(() => import('@/features/settlements/pages/SettlementsPage'));
const WebhooksPage = lazy(() => import('@/features/webhooks/pages/WebhooksPage'));
const ApiKeysPage = lazy(() => import('@/features/api-keys/pages/ApiKeysPage'));
const AuditPage = lazy(() => import('@/features/audit/pages/AuditPage'));
const TeamPage = lazy(() => import('@/features/team/pages/TeamPage'));
const SandboxPage = lazy(() => import('@/features/sandbox/pages/SandboxPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/features/_placeholder/NotFoundPage'));
const AuthCallback = lazy(() => import('@/features/auth/pages/AuthCallback'));
const SignupPage = lazy(() => import('@/features/auth/pages/SignupPage'));

function PageFallback() {
    return (
        <div
            className="container space-y-6 py-8"
            aria-busy="true"
            aria-live="polite"
            aria-label="Loading page"
        >
            <div className="flex flex-col gap-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-full max-w-md" />
            </div>
            <div className="flex flex-col gap-2">
                {Array.from({ length: 5 }).map((_, idx) => (
                    <Skeleton key={idx} className="h-12 w-full" />
                ))}
            </div>
        </div>
    );
}

function withSuspense(node: ReactNode) {
    return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

function gated(path: ProtectedRoutePath, node: ReactNode) {
    return withSuspense(<RequireRole roles={ROUTE_ROLES[path]}>{node}</RequireRole>);
}

export const router = createBrowserRouter([
    { path: '/auth/callback', element: withSuspense(<AuthCallback />) },
    { path: '/signup', element: withSuspense(<SignupPage />) },
    {
        element: <ProtectedShell />,
        children: [
            { index: true, element: withSuspense(<DashboardPage />) },
            { path: 'bookings', element: withSuspense(<BookingsPage />) },
            { path: 'quotes', element: withSuspense(<QuotesPage />) },
            { path: 'settlements', element: withSuspense(<SettlementsPage />) },
            { path: 'webhooks', element: gated('/webhooks', <WebhooksPage />) },
            { path: 'api-keys', element: gated('/api-keys', <ApiKeysPage />) },
            { path: 'audit', element: gated('/audit', <AuditPage />) },
            { path: 'team', element: gated('/team', <TeamPage />) },
            { path: 'sandbox', element: gated('/sandbox', <SandboxPage />) },
            { path: 'settings', element: gated('/settings', <SettingsPage />) },
            { path: '*', element: withSuspense(<NotFoundPage />) },
        ],
    },
]);
